import type { AudioManager } from '../audio/audioManager';
import { BoardRenderer } from '../boardRenderer';
import { TUTORIAL_LEVEL, TUTORIAL_STEPS, TutorialLesson } from '../../core/tutorial';
import type { ThemeName } from '../../core/types';
import { heart, icon } from './icons';

export class TutorialController {
  active = false;
  private board = new BoardRenderer();
  private initialized = false;
  private lesson = new TutorialLesson();
  private busy = true;
  private dailyClockRunning = false;
  private timeout: number | null = null;
  private onFinish: ((status: 'completed' | 'skipped') => void) | null = null;
  private root: HTMLElement;

  constructor(private host: HTMLElement, private audio: AudioManager) {
    this.root = document.createElement('section');
    this.root.id = 'tutorial';
    this.root.className = 'tutorial hidden';
    this.root.setAttribute('aria-label', 'How to play Arrow Master');
    this.root.innerHTML = `
      <header class="tutorial-top"><div><span class="eyebrow">HOW TO PLAY</span><strong>A little practice</strong></div><button id="tutorial-skip" class="tutorial-skip">Skip tutorial</button></header>
      <div class="tutorial-steps" id="tutorial-steps" aria-label="Tutorial progress"></div>
      <div class="tutorial-copy" aria-live="polite" aria-atomic="true"><span class="tutorial-step-label" id="tutorial-step-label"></span><h2 id="tutorial-title" tabindex="-1"></h2><p id="tutorial-copy"></p></div>
      <div class="tutorial-board board-panel" id="tutorial-board" aria-label="Practice arrow board" aria-busy="true"></div>
      <div class="tutorial-success hidden" id="tutorial-success">
        <div class="tutorial-trophy" aria-hidden="true">${icon('trophy')}</div>
        <div class="tutorial-rule"><span class="tutorial-rule-icon">${heart}</span><div><strong>Three hearts</strong><p>A blocked tap costs one heart. Lose all three and the attempt ends.</p></div></div>
        <div class="tutorial-rule"><span class="tutorial-rule-icon">◷</span><div><strong>Beat the clock</strong><p>Clear every arrow before time runs out.</p></div></div>
        <div class="tutorial-rule"><span class="tutorial-rule-icon">↔</span><div><strong>Get a closer look</strong><p>Pinch or scroll to zoom. Drag to explore a larger board.</p></div></div>
        <button class="modal-btn primary" id="tutorial-done">Start playing</button>
      </div>
      <footer class="tutorial-footer"><button id="tutorial-hint" class="tutorial-hint hidden">${icon('hint')}<span>Try a free hint</span></button><p id="tutorial-note">No timer. No lives lost. Take your time.</p><p id="tutorial-daily-note" class="hidden">Your Daily Challenge clock keeps running.</p><span id="tutorial-feedback" role="status" aria-live="polite"></span></footer>
      <button class="tutorial-keyboard-action" id="tutorial-action">Tap the highlighted arrow</button>`;
    host.appendChild(this.root);
    this.find<HTMLButtonElement>('tutorial-skip').addEventListener('click', () => this.finish('skipped'));
    this.find<HTMLButtonElement>('tutorial-done').addEventListener('click', () => this.finish('completed'));
    this.find<HTMLButtonElement>('tutorial-hint').addEventListener('click', () => {
      if (!this.active || this.busy || !this.lesson.useHint()) return;
      this.audio.play('hint');
      this.render();
    });
    this.find<HTMLButtonElement>('tutorial-action').addEventListener('click', () => {
      const target = this.lesson.current?.target;
      if (target) this.tap(target);
    });
  }

  private find<T extends HTMLElement = HTMLElement>(id: string): T { return this.root.querySelector<T>(`#${id}`)!; }

  async start(theme: ThemeName, hintCost: number, doneLabel: string, onFinish: (status: 'completed' | 'skipped') => void, dailyClockRunning = false): Promise<void> {
    if (this.active) return;
    this.active = true;
    this.busy = true;
    this.dailyClockRunning = dailyClockRunning;
    this.onFinish = onFinish;
    this.lesson = new TutorialLesson();
    this.root.dataset.step = 'loading';
    this.root.classList.remove('hidden');
    this.host.classList.add('in-tutorial');
    this.find('tutorial-success').classList.add('hidden');
    this.find('tutorial-board').classList.remove('hidden');
    this.find('tutorial-board').setAttribute('aria-busy', 'true');
    this.find<HTMLButtonElement>('tutorial-skip').disabled = true;
    this.find('tutorial-skip').textContent = doneLabel === 'Back to game' || doneLabel === 'Back to lobby' ? 'Close tutorial' : 'Skip tutorial';
    this.find('tutorial-done').textContent = doneLabel;
    this.find('tutorial-hint').dataset.cost = String(hintCost);
    this.find('tutorial-title').textContent = 'Let’s learn by playing';
    this.find('tutorial-copy').textContent = 'Clear a tiny board, one move at a time.';
    this.find('tutorial-step-label').textContent = 'GUIDED PRACTICE';
    this.find('tutorial-steps').innerHTML = '';
    this.find('tutorial-hint').classList.add('hidden');
    this.find('tutorial-note').textContent = this.practiceNote;
    this.find('tutorial-feedback').textContent = '';
    this.find('tutorial-daily-note').classList.toggle('hidden', !dailyClockRunning);
    this.find<HTMLButtonElement>('tutorial-action').disabled = true;
    try {
      if (!this.initialized) {
        await this.board.init(this.find('tutorial-board'));
        this.initialized = true;
      }
      this.board.app.start();
      this.board.setLevel(TUTORIAL_LEVEL, [...this.lesson.remainingIds], id => this.tap(id), theme);
      this.board.playEntrance(() => {
        this.busy = false;
        this.find<HTMLButtonElement>('tutorial-skip').disabled = false;
        this.find('tutorial-board').setAttribute('aria-busy', 'false');
        this.render();
      });
    } catch (error) {
      this.active = false;
      this.root.classList.add('hidden');
      this.host.classList.remove('in-tutorial');
      this.onFinish = null;
      throw error;
    }
  }

  private render(): void {
    this.root.dataset.step = this.lesson.complete ? 'done' : String(this.lesson.step);
    this.find('tutorial-feedback').textContent = '';
    this.find('tutorial-steps').innerHTML = TUTORIAL_STEPS.map((_, i) => `<span class="${i < this.lesson.step ? 'complete' : i === this.lesson.step ? 'current' : ''}" aria-label="Step ${i + 1}${i < this.lesson.step ? ' complete' : i === this.lesson.step ? ', current' : ''}"></span>`).join('');
    this.find('tutorial-hint').classList.toggle('hidden', this.lesson.complete || this.lesson.current!.action !== 'hint');
    this.find<HTMLButtonElement>('tutorial-action').disabled = this.lesson.complete || this.lesson.current!.action !== 'tap';
    if (this.lesson.complete) {
      this.board.setGuide(null);
      this.board.app.stop();
      this.find('tutorial-board').classList.add('hidden');
      this.find('tutorial-success').classList.remove('hidden');
      this.find('tutorial-step-label').textContent = 'PRACTICE COMPLETE';
      this.find('tutorial-title').textContent = 'You’ve got it!';
      this.find('tutorial-copy').textContent = 'Find a clear path for every arrow. You’re ready for the real thing.';
      this.find('tutorial-note').textContent = `In levels, hints cost ${this.find('tutorial-hint').dataset.cost} coins and highlight a safe move.`;
      this.find('tutorial-skip').classList.add('hidden');
      this.audio.play('complete');
      this.find('tutorial-done').focus();
      return;
    }
    const step = this.lesson.current;
    this.find('tutorial-skip').classList.remove('hidden');
    this.find('tutorial-step-label').textContent = `STEP ${this.lesson.step + 1} OF ${TUTORIAL_STEPS.length}`;
    this.find('tutorial-title').textContent = step!.title;
    this.find('tutorial-copy').textContent = step!.copy;
    this.find('tutorial-note').textContent = step!.action === 'hint'
      ? `Free in practice · ${this.find('tutorial-hint').dataset.cost} coins in levels`
      : this.practiceNote;
    this.board.setGuide(step!.target);
  }

  private get practiceNote(): string {
    return this.dailyClockRunning ? 'Practice uses no coins or hearts.' : 'No timer. No lives lost. Take your time.';
  }

  private tap(id: string): void {
    if (!this.active || this.busy) return;
    const result = this.lesson.tap(id);
    if (result === 'ignored') {
      this.find('tutorial-feedback').textContent = this.lesson.current?.action === 'hint' ? 'Try the free lightbulb hint below.' : 'Try the glowing arrow inside the gold circle.';
      return;
    }
    this.busy = true;
    this.find<HTMLButtonElement>('tutorial-action').disabled = true;
    this.board.setGuide(null);
    if (result === 'blocked') {
      this.board.wobble(id);
      this.audio.play('blocked');
      this.find('tutorial-feedback').textContent = 'Blocked! No heart lost in practice.';
    } else {
      this.board.slide(id);
      this.audio.play('slide');
      this.find('tutorial-feedback').textContent = 'Nice move!';
    }
    this.timeout = window.setTimeout(() => {
      this.timeout = null;
      this.busy = false;
      if (this.active) this.render();
    }, result === 'blocked' ? 600 : 850);
  }

  private finish(status: 'completed' | 'skipped'): void {
    if (!this.active || !this.initialized || (status === 'completed' && !this.lesson.complete)) return;
    if (this.timeout !== null) window.clearTimeout(this.timeout);
    this.timeout = null;
    this.board.clear();
    this.board.app.stop();
    this.active = false;
    this.root.classList.add('hidden');
    this.host.classList.remove('in-tutorial');
    const callback = this.onFinish;
    this.onFinish = null;
    callback?.(status);
  }
}
